// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.InputByDataTypeRegistration.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
import editorFactories from 'share/factories/Izenda.PropertiesFieldEditorFactories';
import classnames from 'classnames';
import { getOr, concat, has, omit, isNil, invokeArgs, isNaN } from 'lodash/fp';
import { notifyChange, CHECKBOX, INPUT, COLOR, DROPDOWN, DATE } from 'EventUtils';

import Validator from 'share/components/hoc/Izenda.Validator';
import Input from './Izenda.Input';
import PopupInput from './Izenda.PopupInput';
import ButtonGroup from './Izenda.ButtonGroup';
import NumericInput from './Izenda.NumericInput';
import CustomInput from 'share/components/controls/Izenda.CustomInput';
import ColorPicker from './Izenda.ColorPicker';
import DropDown from './Izenda.DropDown';
import DateTimePicker from './DateTime/Izenda.DateTimePicker';
import DateTimePickerByDateElement from './Izenda.DateTimePickerByDateElement';
import OverflowText from './Izenda.OverflowText';

const ValidatorInput = Validator()(Input);
const ValidatorNumericInput = Validator()(NumericInput);
const ValidatorDropDown = Validator()(DropDown);
const ValidatorDateTimePicker = Validator()(DateTimePicker);
const ValidatorDateTimePickerByDateElement = Validator()(DateTimePickerByDateElement);
const ValidatorCustomInput = Validator()(CustomInput);

// Auto registering the built-in editor types
(() => {
  editorFactories.register('checkbox', createOptionControl);
  editorFactories.register('radio', createOptionControl);
  editorFactories.register('text', createTextInputControl);
  editorFactories.register('password', createTextInputControl);
  editorFactories.register('file', createTextInputControl);
  editorFactories.register('number', createNumberInputControl);
  editorFactories.register('color', createColorPickerControl);
  editorFactories.register('select', createSelectControl);
  editorFactories.register('time', createTimePickerControl);
  editorFactories.register('date', createDatePickerControl);
  editorFactories.register('datetime', createDateTimePickerControl);
  editorFactories.register('static', createStaticTextControl);
  editorFactories.register('btnPopup', createButtonPopupControl);
  editorFactories.register('btnGroup', createButtonGroupControl);
  editorFactories.register('customInput', createCustomInputControl);
})();

function createOptionControl(type, props) {
  const { value, onChange } = props;
  const hasValidation = has('validations', props);

  return {
    props: {
      type: type,
      className: 'default',
      containerClassName: type,
      checked: !!value,
      onChange: notifyChange(onChange, CHECKBOX)
    },
    component: hasValidation ? ValidatorInput : Input
  };
}

function createTextInputControl(type, props) {
  const { className, onChange } = props;
  const hasValidation = has('validations', props);
  return {
    props: {
      className: classnames('form-control', className),
      containerClassName: '',
      onChange: notifyChange(onChange, INPUT)
    },
    component: hasValidation ? ValidatorInput : Input
  };
}

function createNumberInputControl(type, props) {
  const { className, onChange } = props;
  const hasValidation = has('validations', props);
  return {
    props: {
      className: className,
      onChange: notifyChange(onChange)
    },
    component: hasValidation ? ValidatorNumericInput : NumericInput
  };
}

function createColorPickerControl(type, props) {
  const { className, onChange } = props;
  return {
    props: {
      className: className,
      onChange: notifyChange(onChange, COLOR)
    },
    component: ColorPicker
  };
}

function createSelectControl(type, props) {
  const { className, onChange } = props;
  const hasValidation = has('validations', props);
  return {
    props: {
      className: className,
      onChange: notifyChange(onChange, DROPDOWN)
    },
    component: hasValidation ? ValidatorDropDown : DropDown
  };
}

function createTimePickerControl(type, props) {
  const { className, onChange } = props;
  const hasValidation = has('validations', props);

  return {
    props: {
      className: className,
      calendar: false,
      onChange: notifyChange(onChange, DATE)
    },
    component: hasValidation ? ValidatorDateTimePicker : DateTimePicker
  };
}

function createDatePickerControl(type, props) {
  const { className, onChange } = props;
  const hasValidation = has('validations', props);

  return {
    props: {
      className: className,
      time: false,
      onChange: notifyChange(onChange, DATE)
    },
    component: hasValidation ? ValidatorDateTimePicker : DateTimePicker
  };
}

function createDateTimePickerControl(type, props) {
  const { className, onChange, groupByDateTime } = props;
  const hasValidation = has('validations', props);

  return {
    props: {
      className: className,
      groupByDateTime: groupByDateTime,
      onChange: notifyChange(onChange, DATE)
    },
    component: hasValidation ? ValidatorDateTimePickerByDateElement : DateTimePickerByDateElement
  };
}

function createStaticTextControl(type, props) {
  const { className, value } = props;
  return {
    props: {
      className: className,
      tooltip: value
    },
    child: value,
    component: OverflowText
  };
}

function createButtonPopupControl(type, props) {
  const { className, onChange, onChangePopup } = props;
  return {
    props: {
      className: className,
      onClosed: onChange,
      onChange: onChangePopup
    },
    component: PopupInput
  };
}

function createButtonGroupControl(type, props) {
  const { className, onChange } = props;
  return {
    props: {
      className: className,
      onChange: onChange
    },
    component: ButtonGroup
  };
}

function createCustomInputControl(type, props) {
  const { className, onChange } = props;
  return {
    props: {
      className: classnames('form-control', className),
      containerClassName: '',
      onChange: notifyChange(onChange, INPUT)
    },
    component: hasValidation ? ValidatorCustomInput : CustomInput
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.InputByDataTypeRegistration.js